import { setupWorker } from 'msw/browser';
import { http, HttpResponse, RequestHandler } from 'msw';
import { ApiUserProfileRequest } from '@allbin/mobilix-api-client';
import { DateTime } from 'luxon';

import { tdApiCheckInPlans } from '../testdata/checkins';
import { tdApiRecurringPlans } from '../testdata/recurring';
import { tdApiTemplates } from '../testdata/templates';
import { tdApiWorkorders, tdWorkorderTags } from '../testdata/workorders';
import { tdContractors } from '../testdata/contractors';
import { tdEntityEvents, tdGetApiWorkOrderEvents } from '../testdata/events';
import { tdEntityTypes, tdGetEntities } from '../testdata/entities';
import { tdErrorReports } from '../testdata/errorreports';
import { tdFilterSets } from '../testdata/filter';
import { tdGetEntityChangesets } from '../testdata/changesets';
import { tdSchema } from '../testdata/schema';
import {
  tdUserIdAdmin,
  tdUserIdContractorAdmin,
  tdUsers,
} from '../testdata/users';
import {
  TestUserWorkorderAdminPermissions,
  TestUserWorkorderContractorAdminPermissions,
} from '../testdata/permissions';
import { tdAllLicensesGroup } from '../testdata/licenses';
import { tdTenants } from '../testdata/tenants';
import { tdAllAuthProfiles } from '../testdata/profiles';
import { buildStubToken } from '../providers/StubAllAuthProvider';

import { scenarios } from './scenarios';

let profile = {};

const gtfsBaseUrls = [
  'https://gtfs.vl.allbin.se',
  'https://gtfs-trafiklab-adapter.allbin.se/sormland',
  'https://gtfs-trafiklab-adapter.allbin.se/vasterbotten',
];

const gtfsTablePeriods = [
  {
    id: 1,
    name: 'MSW',
    provider: 'msw',
    date_start: '2020-01-01',
    date_end: null,
  },
];

const rests: RequestHandler[] = [
  ...gtfsBaseUrls.flatMap((baseUrl) => [
    http.get(`${baseUrl}/table_periods`, () =>
      HttpResponse.json(gtfsTablePeriods),
    ),
    http.get(`${baseUrl}/all_distinct_stops`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/routes`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/route_stops`, () => HttpResponse.json([])),
    http.get(`${baseUrl}/stop_link_shapes`, () => HttpResponse.json([])),
  ]),
  http.get('https://api.mobilix.dev.allbin.se/tags', () =>
    HttpResponse.json(tdWorkorderTags),
  ),
  http.get('https://api.mobilix.dev.allbin.se/workorders', () =>
    HttpResponse.json(tdApiWorkorders),
  ),
  http.get<{ workOrderId: string }>(
    'https://api.mobilix.dev.allbin.se/workorders/:workOrderId',
    ({ params }) => {
      const wo = tdApiWorkorders.find((wo) => wo.id === params.workOrderId);
      if (!wo) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(wo);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/entities', () =>
    HttpResponse.json(tdGetEntities()),
  ),
  http.post<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/events',
    ({ params }) => {
      const events = tdEntityEvents.filter(
        (ev) => ev.entity_id === params.entityId,
      );
      if (events.length === 0) {
        return new HttpResponse(null, { status: 500 });
      }
      return HttpResponse.json(events[0]);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/entity_types', () =>
    HttpResponse.json(tdEntityTypes),
  ),
  http.get('https://api.mobilix.dev.allbin.se/contractors', () =>
    HttpResponse.json(tdContractors),
  ),
  http.get<{ contractorId: string }>(
    'https://api.mobilix.dev.allbin.se/contractors/:contractorId',
    ({ params }) =>
      HttpResponse.json(
        tdContractors.find((c) => c.id === params.contractorId),
      ),
  ),
  http.get('https://api.mobilix.dev.allbin.se/tenants', () =>
    HttpResponse.json(tdTenants),
  ),
  // Mirrors the migrated API: `ids` is mandatory and unknown ids come back
  // as '<Deleted user>' placeholder rows, so lookups never 404.
  http.get('https://api.mobilix.dev.allbin.se/users', ({ request }) => {
    const idsParam = new URL(request.url).searchParams.get('ids');
    if (!idsParam) {
      return new HttpResponse(null, { status: 400 });
    }
    const users = idsParam.split(',').map(
      (id) =>
        tdUsers.find((u) => u.id === id) ?? {
          id,
          user_id: id,
          name: '<Deleted user>',
          email: '',
          meta: {
            created_at: DateTime.now().toISO(),
            created_by: id,
            updated_at: DateTime.now().toISO(),
          },
        },
    );
    return HttpResponse.json(users);
  }),
  http.get('https://api.mobilix.dev.allbin.se/entity_schemas', () =>
    HttpResponse.json([tdSchema]),
  ),
  http.get('https://api.mobilix.dev.allbin.se/feature_licenses', () =>
    HttpResponse.json([tdAllLicensesGroup]),
  ),
  http.get<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/changesets',
    ({ params }) => {
      const entity = tdGetEntities().find((e) => e.id === params.entityId);
      if (!entity) {
        return new HttpResponse(null, { status: 404 });
      }
      const changesets = tdGetEntityChangesets(
        params.entityId,
        entity.properties,
      );
      if (!changesets) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(changesets);
    },
  ),
  http.get<{ entityId: string }>(
    'https://api.mobilix.dev.allbin.se/entities/:entityId/events',
    ({ params }) => {
      console.log('tdEntityEvents', tdEntityEvents);
      const events = tdEntityEvents.filter(
        (ev) => ev.entity_id === params.entityId,
      );
      return HttpResponse.json(events);
    },
  ),
  http.get<{ workOrderId: string }>(
    'https://api.mobilix.dev.allbin.se/workorders/:workOrderId/events',
    ({ params }) =>
      HttpResponse.json(tdGetApiWorkOrderEvents(params.workOrderId)),
  ),
  http.get('https://api.mobilix.dev.allbin.se/filtersets', () =>
    HttpResponse.json(tdFilterSets),
  ),
  http.get('https://api.mobilix.dev.allbin.se/user_profile', () =>
    HttpResponse.json({
      meta: {
        created_at: DateTime.now().toISO(),
        created_by: tdUserIdAdmin,
        updated_at: DateTime.now().toISO(),
      },
      profile,
    }),
  ),
  http.put<never, ApiUserProfileRequest>(
    'https://api.mobilix.dev.allbin.se/user_profile',
    async ({ request }) => {
      const body = await request.json();
      profile = body ? body.profile : profile;
      return HttpResponse.json({
        meta: {
          created_at: DateTime.now().toISO(),
          created_by: tdUserIdAdmin,
          updated_at: DateTime.now().toISO(),
        },
        profile,
      });
    },
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/error_reports',
    () => new HttpResponse(null, { status: 500 }),
  ),
  http.get('https://api.mobilix.dev.allbin.se/error_reports', () =>
    HttpResponse.json(
      tdErrorReports.filter((er) => er.meta.deleted_at === undefined),
    ),
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/error_reports/:id',
    ({ params }) => {
      const er = tdErrorReports.find((er) => er.id === params.id);
      if (!er) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(er);
    },
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/checkin_plans/:id',
    ({ params }) => {
      const plan = tdApiCheckInPlans.find((plan) => plan.id === params.id);
      if (!plan) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(plan);
    },
  ),
  http.post(
    'https://api.mobilix.dev.allbin.se/checkin_plans',
    () => new HttpResponse(null, { status: 500 }),
  ),
  http.put(
    'https://api.mobilix.dev.allbin.se/checkin_plans/:id',
    () => new HttpResponse(null, { status: 404 }),
  ),
  http.get('https://api.mobilix.dev.allbin.se/checkin_plans', () =>
    HttpResponse.json(tdApiCheckInPlans),
  ),
  http.get<{ id: string }>(
    'https://api.mobilix.dev.allbin.se/recurring_workorder_plans/:id',
    ({ params }) => {
      const plan = tdApiRecurringPlans.find((plan) => plan.id === params.id);
      if (!plan) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(plan);
    },
  ),
  http.get('https://api.mobilix.dev.allbin.se/recurring_workorder_plans', () =>
    HttpResponse.json(tdApiRecurringPlans),
  ),
  http.get('https://api.mobilix.dev.allbin.se/workorder_instructions', () =>
    HttpResponse.json(tdApiTemplates),
  ),
  // AllAuth endpoints — only exercised by the gallery's profile-selector
  // entry; app auth in msw/gallery modes is bypassed by StubAllAuthProvider.
  http.get('https://api.auth.dev.allbin.se/profiles', () =>
    HttpResponse.json(tdAllAuthProfiles),
  ),
  http.get('https://api.auth.dev.allbin.se/profiles/token', ({ request }) => {
    const profileId = new URL(request.url).searchParams.get('user_profile_id');
    const profile = tdAllAuthProfiles.find((p) => p.id === profileId);
    if (!profile) {
      return new HttpResponse(null, { status: 404 });
    }
    if (!profile.active) {
      return new HttpResponse(null, { status: 400 });
    }
    const isContractorProfile = profile.organization_id !== undefined;
    return HttpResponse.json({
      access_token: buildStubToken({
        sub: isContractorProfile ? tdUserIdContractorAdmin : tdUserIdAdmin,
        email: profile.email,
        tenant_id: profile.tenant_id,
        organization_id: profile.organization_id ?? null,
        permissions: isContractorProfile
          ? TestUserWorkorderContractorAdminPermissions
          : TestUserWorkorderAdminPermissions,
        user_profile_id: profile.id,
      }),
    });
  }),
];

function resolveScenarioOverrides(): RequestHandler[] {
  const name = new URLSearchParams(window.location.search).get('msw_scenario');
  if (!name) return [];
  const found = Object.hasOwn(scenarios, name) ? scenarios[name] : undefined;
  if (!found) {
    console.warn(
      `[msw] unknown scenario "${name}" — falling back to defaults (available: ${Object.keys(
        scenarios,
      ).join(', ')})`,
    );
    return [];
  }
  console.info(
    `[msw] scenario "${name}" active (${found.length} override handler${
      found.length === 1 ? '' : 's'
    })`,
  );
  return found;
}

export const mswWorker = setupWorker(...resolveScenarioOverrides(), ...rests);
